'use client';

import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { Globe, Mic, Smartphone } from 'lucide-react';
import { useInView } from 'motion/react';

import Logo from '@/components/bazak-logo';
import { useMediaQuery } from '@/hooks/use-media-query';
import { BRAND_GLYPHS } from '@/lib/brand-glyphs';
import { PLATFORM_COPY } from '@/lib/content';
import { CARD_HOVER, cn } from '@/lib/utils';

type SurfaceName = (typeof PLATFORM_COPY.surfaces)[number];
type CycleName = (typeof PLATFORM_COPY.cycle)[number];

/** One place in the loop, and how its packet travels. Shared by both layouts. */
const SURFACE_TRACK: Record<
  SurfaceName,
  { lane: 0 | 1; slot: 0 | 1 | 2; dur: string; delay: string }
> = {
  Claude: { lane: 0, slot: 0, dur: '2.6s', delay: '0s' },
  ChatGPT: { lane: 1, slot: 0, dur: '2.9s', delay: '2.1s' },
  'Your website': { lane: 0, slot: 1, dur: '3.1s', delay: '1.3s' },
  'Branded App': { lane: 1, slot: 1, dur: '2.4s', delay: '1.7s' },
  WhatsApp: { lane: 0, slot: 2, dur: '2.2s', delay: '0.6s' },
  'Voice Agents': { lane: 1, slot: 2, dur: '3.3s', delay: '0.9s' },
};

type Rect = { x: number; y: number; w: number; h: number };
type Track = { lane: 0 | 1; slot: 0 | 1 | 2 };

function roundedRectPath({ rect, radius = 22 }: { rect: Rect; radius?: number }) {
  const { x, y, w, h } = rect;
  const r = radius;
  return `M ${x + r} ${y} H ${x + w - r} A ${r} ${r} 0 0 1 ${x + w} ${y + r} V ${y + h - r} A ${r} ${r} 0 0 1 ${x + w - r} ${y + h} H ${x + r} A ${r} ${r} 0 0 1 ${x} ${y + h - r} V ${y + r} A ${r} ${r} 0 0 1 ${x + r} ${y}`;
}

function inset({ rect, by }: { rect: Rect; by: number }): Rect {
  return { x: rect.x + by, y: rect.y + by, w: rect.w - by * 2, h: rect.h - by * 2 };
}

/** The four capability groups, one per corner of the hub's filled block. */
function chipCorners({ block, canvasW }: { block: Rect; canvasW: number }) {
  const start = { left: block.x + 16 };
  const end = { right: canvasW - block.x - block.w + 16 };
  const top = block.y + 18;
  const bottom = block.y + block.h - 40;
  return {
    IMPROVE: { style: { top, ...start }, rows: 'justify-start' },
    DEPLOY: { style: { top, ...end }, rows: 'justify-end' },
    LEARN: { style: { top: bottom, ...start }, rows: 'justify-start' },
    CONVERSE: { style: { top: bottom, ...end }, rows: 'justify-end' },
  } satisfies Record<CycleName, { style: CSSProperties; rows: string }>;
}

/** The four stage labels, on the corners of the dashed ring. `by` insets them off each corner. */
function cycleCorners({ hub, by }: { hub: Rect; by: number }) {
  const [left, right] = [hub.x + by, hub.x + hub.w - by];
  const [top, bottom] = [hub.y, hub.y + hub.h];
  return {
    IMPROVE: { top, left },
    DEPLOY: { top, left: right },
    CONVERSE: { top: bottom, left: right },
    LEARN: { top: bottom, left },
  } satisfies Record<CycleName, { top: number; left: number }>;
}

type Layout = {
  w: number;
  h: number;
  /** The dashed ring; the filled block is this inset, and the cycle labels sit on its corners. */
  hub: Rect;
  brand: { top: number; left: number; width: number; translate: string };
  feed: string;
  feedArrow: string;
  logo: { top: number; left: number };
  cycleAt: Record<CycleName, { top: number; left: number }>;
  chipAt: Record<CycleName, { style: CSSProperties; rows: string }>;
  nodeAt: (track: Track) => { top: number; left: number };
  rayFor: (track: Track) => string;
  /** Fixed geometry, derived once per layout — the canvas re-renders on every resize frame. */
  block: Rect;
  orbit: string;
  rays: { d: string; dur: string; delay: string }[];
};

function withGeometry(base: Omit<Layout, 'block' | 'orbit' | 'rays'>): Layout {
  return {
    ...base,
    block: inset({ rect: base.hub, by: 14 }),
    orbit: roundedRectPath({ rect: base.hub }),
    rays: Object.values(SURFACE_TRACK).map(({ lane, slot, dur, delay }) => ({
      d: base.rayFor({ lane, slot }),
      dur,
      delay,
    })),
  };
}

/** Brand above, hub, surfaces below in two columns. The narrow-column shape. */
function portraitLayout(): Layout {
  const hub: Rect = { x: 18, y: 208, w: 344, h: 196 };
  const w = 380;
  const laneX = [100, 280] as const;
  const slotY = [472, 532, 592] as const;

  return withGeometry({
    w,
    h: 640,
    hub,
    brand: { top: 148, left: 190, width: 280, translate: '-translate-x-1/2 -translate-y-full' },
    feed: 'M 190 152 V 196',
    feedArrow: 'M 190 152 V 200',
    logo: { top: 306, left: 190 },
    cycleAt: cycleCorners({ hub, by: 38 }),
    chipAt: chipCorners({ block: inset({ rect: hub, by: 14 }), canvasW: w }),
    nodeAt: ({ lane, slot }) => ({ top: slotY[slot], left: laneX[lane] }),
    rayFor: ({ lane, slot }) => {
      // Fan the three on a side apart so they stay legible where they leave the hub.
      const fanX = lane === 0 ? 135 + slot * 20 : 245 - slot * 20;
      const endX = lane === 0 ? 118 : 262;
      const y = slotY[slot];
      return `M ${fanX} ${hub.y + hub.h} V ${y - 10} Q ${fanX} ${y} ${endX} ${y} H ${endX}`;
    },
  });
}

/**
 * The same diagram turned on its side: brand left, hub centre, surfaces right in two rows. Used
 * where the section is stacked and the band is wide — there the portrait shape has to be capped to
 * ~400px or its height runs off the screen, which wastes most of the row.
 */
function landscapeLayout(): Layout {
  const hub: Rect = { x: 270, y: 60, w: 300, h: 260 };
  const mid = hub.y + hub.h / 2;
  const right = hub.x + hub.w;
  const slotX = [670, 790, 910] as const;
  const laneY = [110, 270] as const;
  const w = 960;

  return withGeometry({
    w,
    h: 380,
    hub,
    brand: { top: mid, left: 128, width: 220, translate: '-translate-x-1/2 -translate-y-1/2' },
    feed: `M 244 ${mid} H ${hub.x - 4}`,
    feedArrow: `M 244 ${mid} H ${hub.x}`,
    logo: { top: mid, left: hub.x + hub.w / 2 },
    cycleAt: cycleCorners({ hub, by: 44 }),
    chipAt: chipCorners({ block: inset({ rect: hub, by: 14 }), canvasW: w }),
    nodeAt: ({ lane, slot }) => ({ top: laneY[lane], left: slotX[slot] }),
    rayFor: ({ lane, slot }) => {
      // Fan the three in a row apart so the far one passes the near ones rather than through them.
      const fanY = lane === 0 ? mid - 6 - slot * 16 : mid + 6 + slot * 16;
      // The node is a disc over a label, so stop at the edge of that block, not at its centre.
      const endY = lane === 0 ? laneY[0] + 28 : laneY[1] - 28;
      const x = slotX[slot];
      return `M ${right} ${fanY} H ${x - 10} Q ${x} ${fanY} ${x} ${endY} V ${endY}`;
    },
  });
}

const PORTRAIT = portraitLayout();
const LANDSCAPE = landscapeLayout();

function StrokeGlyph({ children, color }: { children: ReactNode; color: string }) {
  return (
    <span className="inline-flex size-[18px] items-center justify-center" style={{ color }}>
      {children}
    </span>
  );
}

function BrandGlyph({ path, color }: { path: string; color: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-[18px]" fill={color}>
      <path d={path} />
    </svg>
  );
}

const SURFACE_MARK: Record<SurfaceName, ReactNode> = {
  Claude: <BrandGlyph path={BRAND_GLYPHS.Claude} color="#D97757" />,
  ChatGPT: <BrandGlyph path={BRAND_GLYPHS.ChatGPT} color="#10A37F" />,
  'Your website': (
    <StrokeGlyph color="#2563EB">
      <Globe aria-hidden="true" className="size-[18px]" strokeWidth={1.8} />
    </StrokeGlyph>
  ),
  'Branded App': (
    <StrokeGlyph color="#7C3AED">
      <Smartphone aria-hidden="true" className="size-[18px]" strokeWidth={1.8} />
    </StrokeGlyph>
  ),
  WhatsApp: <BrandGlyph path={BRAND_GLYPHS.WhatsApp} color="#25D366" />,
  'Voice Agents': (
    <StrokeGlyph color="#D97706">
      <Mic aria-hidden="true" className="size-[18px]" strokeWidth={1.8} />
    </StrokeGlyph>
  ),
};

function Chip({ children, inverted = false }: { children: string; inverted?: boolean }) {
  return (
    <span
      className={cn(
        'rounded-[4px] border px-1.5 py-0.5 font-mono text-2xs tracking-label uppercase',
        inverted ? 'border-background/35 text-background' : 'border-foreground/35 text-foreground',
      )}
    >
      {children}
    </span>
  );
}

function SurfaceNode({
  label,
  glyph,
  layout,
}: {
  label: SurfaceName;
  glyph: ReactNode;
  layout: Layout;
}) {
  return (
    <div
      className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1"
      style={layout.nodeAt(SURFACE_TRACK[label])}
    >
      {/* Identical plate for every surface: colour belongs to the mark, not to what it sits on.
          Two coloured plates among six read as those two being endorsed. */}
      <span
        title={label}
        className="inline-flex size-9 items-center justify-center rounded-full border border-border bg-background"
      >
        {glyph}
      </span>
      <span className="font-mono text-2xs tracking-label whitespace-nowrap text-muted-foreground uppercase">
        {label}
      </span>
    </div>
  );
}

function DiagramCanvas({ layout, running }: { layout: Layout; running: boolean }) {
  const { w, h, hub, block, orbit, rays } = layout;

  // Inline, not a `paused` class: these set their animation with the `animation` shorthand, which
  // resets `animation-play-state` to `running`, and inline beats any class rule — so a class here
  // is silently a no-op. `offset-distance` is not a compositor property, so this is worth gating.
  const playState = running ? 'running' : 'paused';

  return (
    <div className="relative" style={{ width: w, height: h }}>
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        aria-hidden="true"
        className="absolute inset-0"
      >
        <path d={layout.feed} stroke="var(--foreground)" strokeWidth="2" fill="none" />
        <polygon
          points="0,-4.5 9,0 0,4.5"
          fill="var(--foreground)"
          className="motion-reduce:hidden"
          style={{
            offsetPath: `path('${layout.feedArrow}')`,
            offsetRotate: 'auto',
            animation: 'packet 1.8s linear infinite',
            animationPlayState: playState,
          }}
        />
        {['0s', '-3.5s'].map(delay => (
          <polygon
            key={delay}
            points="0,-4.5 9,0 0,4.5"
            fill="var(--foreground)"
            className="motion-reduce:hidden"
            style={{
              offsetPath: `path('${orbit}')`,
              offsetRotate: 'auto',
              animation: `orbit 7s linear ${delay} infinite`,
              animationPlayState: playState,
            }}
          />
        ))}
        <rect
          x={hub.x}
          y={hub.y}
          width={hub.w}
          height={hub.h}
          rx="22"
          fill="none"
          stroke="var(--faint-foreground)"
          strokeWidth="1.5"
          strokeDasharray="2 6"
        />
        <rect
          x={block.x}
          y={block.y}
          width={block.w}
          height={block.h}
          rx="16"
          fill="var(--foreground)"
        />
        {rays.map(ray => (
          <path key={ray.d} d={ray.d} stroke="var(--border)" strokeWidth="1.5" fill="none" />
        ))}
        {rays.map(ray => (
          <circle
            key={ray.d}
            r="2.2"
            fill="var(--foreground)"
            className="motion-reduce:hidden"
            style={{
              offsetPath: `path('${ray.d}')`,
              animation: `packet ${ray.dur} ease-in-out ${ray.delay} infinite alternate`,
              animationPlayState: playState,
            }}
          />
        ))}
      </svg>

      <div
        className={cn(
          'absolute flex flex-col items-center gap-1.5 rounded-lg border-[1.5px] border-brand bg-background px-3 py-3 text-center',
          layout.brand.translate,
        )}
        style={{ top: layout.brand.top, left: layout.brand.left, width: layout.brand.width }}
      >
        <p className="font-mono text-base font-semibold tracking-label">{PLATFORM_COPY.brand}</p>
        <p className="font-mono text-2xs leading-snug font-semibold tracking-label text-muted-foreground uppercase">
          {PLATFORM_COPY.brandItems}
        </p>
      </div>

      {/* The mark itself, not the word set in mono — it is the one place on the page the logo can
          stand for the product rather than describe it. */}
      <div
        className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2"
        style={layout.logo}
      >
        <Logo aria-hidden="true" className="h-6 w-auto" fill="var(--background)" />
        {/* Names the hub: the mark alone leaves the block unlabelled. */}
        <p className="font-mono text-2xs tracking-label text-background/60">THE OPERATING SYSTEM</p>
      </div>

      {/* Capabilities sit in the corner nearest the stage they serve, so the hub reads as four
          quadrants of one loop instead of a stack of unrelated tags. */}
      {PLATFORM_COPY.cycle.map(stage => (
        <div key={stage} className="absolute" style={layout.chipAt[stage].style}>
          <div className={cn('flex max-w-[104px] flex-wrap gap-1', layout.chipAt[stage].rows)}>
            {PLATFORM_COPY.osChipsByStage[stage].map(chip => (
              <Chip key={chip} inverted>
                {chip}
              </Chip>
            ))}
          </div>
        </div>
      ))}

      {/* Numbered, so the ring reads as an order. Two arrowheads orbit it continuously, but
          nothing said IMPROVE came before DEPLOY — they looked like four captions near a loop
          rather than four stops on one. */}
      {PLATFORM_COPY.cycle.map((label, index) => (
        <div
          key={label}
          className="absolute flex -translate-x-1/2 -translate-y-1/2 items-baseline gap-1.5 bg-card px-1.5 py-0.5 font-mono text-xs font-semibold tracking-label"
          style={layout.cycleAt[label]}
        >
          <span className="text-2xs font-normal text-brand">0{index + 1}</span>
          {label}
        </div>
      ))}

      {PLATFORM_COPY.surfaces.map(surface => (
        <SurfaceNode key={surface} label={surface} glyph={SURFACE_MARK[surface]} layout={layout} />
      ))}
    </div>
  );
}

const FIGURE_LABEL = `${PLATFORM_COPY.brand} feeds Bazak, which deploys the same agent to ${PLATFORM_COPY.surfaces.join(', ')}. The loop is ${PLATFORM_COPY.cycle.join(', ')}.`;

/**
 * Landscape only where the section is stacked *and* the band is wide enough to carry it: on a
 * phone the sideways shape would scale to a third of its size and nothing on it would be legible.
 */
const LANDSCAPE_MEDIA = '(min-width: 860px) and (max-width: 1023px)';

/** Brand → Bazak hub → every surface, scaled to the card box it is given. */
export function ArchitectureDiagram({ className }: { className?: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const figureRef = useRef<HTMLElement>(null);
  const inView = useInView(figureRef, { margin: '200px' });
  const [scale, setScale] = useState(1);
  // Portrait on the server: it is what the lg column and the phone both get, so only the middle
  // band pays for the correction, and the figure is well below the fold.
  const layout = useMediaQuery({ query: LANDSCAPE_MEDIA }) ? LANDSCAPE : PORTRAIT;
  const { w, h } = layout;

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const apply = () => {
      const { width, height } = frame.getBoundingClientRect();
      if (width < 1) return;
      const byWidth = width / w;
      const naturalHeight = width * (h / w);
      // Stretched lg column is taller than the canvas; contain so both edges meet the card.
      setScale(height > naturalHeight + 8 ? Math.min(byWidth, height / h) : byWidth);
    };
    const observer = new ResizeObserver(apply);
    observer.observe(frame);
    apply();
    return () => observer.disconnect();
  }, [w, h]);

  return (
    <figure
      ref={figureRef}
      dir="ltr"
      tabIndex={-1}
      aria-label={FIGURE_LABEL}
      className={cn(
        // min-w-0: without it the flex item floors at the unscaled canvas width, the measurement
        // that drives `scale` reads that instead of the column, and the card overflows the phone.
        'flex h-auto min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border bg-card p-2 lg:h-full',
        // Portrait stacked would run its height off the screen at full band width, so it is capped
        // near its natural size. Landscape is the reason the cap can lift — it wants the band.
        'max-lg:mx-auto max-lg:w-full',
        layout === PORTRAIT && 'max-lg:max-w-[400px]',
        CARD_HOVER,
        className,
      )}
    >
      <div
        ref={frameRef}
        className="flex w-full items-center justify-center lg:min-h-0 lg:flex-1"
        style={{ aspectRatio: `${w}/${h}` }}
      >
        {/* Sized to the *scaled* canvas so the flex parent can centre it. Scaling alone leaves the
            element's layout box at full size, which pins it to the top of a taller column. */}
        <div className="relative" style={{ width: w * scale, height: h * scale }}>
          <div
            className="absolute top-0 left-0 origin-top-left"
            style={{ width: w, height: h, transform: `scale(${scale})` }}
          >
            <DiagramCanvas layout={layout} running={inView} />
          </div>
        </div>
      </div>
    </figure>
  );
}
